<template>
  <div>
    <div class="row mb-3">
      <div class="col-12">
        <h4>Gestion des paramètres</h4>
      </div>
    </div>

    <!-- Search and Filter Section -->
    <div class="row mb-3">
      <div class="col-md-4 mb-2">
        <label>Rechercher</label>
        <input 
          type="text" 
          v-model="searchQuery" 
          class="form-control" 
          placeholder="Rechercher par clé, référence ou valeur..."
          @input="filterSettings"
        />
      </div>
      <div class="col-md-3 mb-2">
        <label>Filtrer par type</label>
        <select v-model="filterType" class="form-select" @change="filterSettings">
          <option value="">Tous les types</option>
          <option value="string">String</option>
          <option value="number">Number</option>
          <option value="boolean">Boolean</option>
          <option value="array">Array</option>
          <option value="object">Object</option>
        </select>
      </div>
      <div class="col-md-5 mb-2 d-flex align-items-end">
        <div class="me-2">
          <button @click="clearFilters()" class="btn btn-outline-secondary btn-sm">
            <i class="bi bi-x-circle"></i> Réinitialiser
          </button>
        </div>
        <div class="ms-auto">
          <span class="badge bg-info">{{ filteredSettings.length }} paramètre(s)</span>
        </div>
      </div>
    </div>

    <div class="row justify-content-end mb-3">
      <div class="col-auto">
        <button @click="refresh()" class="btn btn-secondary" :disabled="loading">
          <i v-if="loading" class="spinner-border spinner-border-sm me-2"></i>
          <i v-else class="bi bi-arrow-clockwise"></i>
          {{ loading ? 'Actualisation...' : 'Actualiser' }}
        </button>
      </div>
      <div class="col-auto">
        <button 
          @click="seedDefaults()" 
          class="btn btn-info" 
          :disabled="loading || seeding"
          title="Installer les paramètres par défaut"
        >
          <i v-if="seeding" class="spinner-border spinner-border-sm me-2"></i>
          <i v-else class="bi bi-download"></i>
          {{ seeding ? 'Installation...' : 'Installer les paramètres par défaut' }}
        </button>
      </div>
      <div class="col-auto">
        <button @click="$router.push({ name: 'settings-create' })" class="btn btn-primary">
          <i class="bi bi-plus-square"></i>
          Créer un nouveau paramètre
        </button>
      </div>
    </div>

    <div v-if="filteredSettings.length === 0 && !loading" class="alert alert-info">
      <i class="bi bi-info-circle"></i> 
      <span v-if="searchQuery || filterType">
        Aucun paramètre ne correspond à votre recherche.
      </span>
      <span v-else>
        Aucun paramètre trouvé. Créez-en un nouveau pour commencer.
      </span>
    </div>

    <div v-else class="table-responsive">
      <table class="table table-striped table-hover">
        <thead class="table-dark">
          <tr>
            <th scope="col">#</th>
            <th scope="col">Référence</th>
            <th scope="col">Clé</th>
            <th scope="col">Type</th>
            <th scope="col">Valeur</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(setting, index) in filteredSettings" :key="setting.reference">
            <td>{{ index + 1 }}</td>
            <td>
              <code>{{ setting.reference }}</code>
            </td>
            <td>
              <strong>{{ setting.key }}</strong>
            </td>
            <td>
              <span class="badge" :class="getTypeBadgeClass(setting.valueType)">
                {{ setting.valueType }}
              </span>
            </td>
            <td>
              <span v-if="setting.valueType === 'boolean'" class="badge" :class="setting.value === 'true' || setting.value === true ? 'bg-success' : 'bg-secondary'">
                {{ setting.value === 'true' || setting.value === true ? 'True' : 'False' }}
              </span>
              <span v-else-if="setting.valueType === 'array' || setting.valueType === 'object'" class="text-muted">
                <code style="font-size: 0.85em;">{{ truncateValue(setting.value, 50) }}</code>
                <button 
                  @click="viewFullValue(setting)" 
                  class="btn btn-link btn-sm p-0 ms-1"
                  title="Voir la valeur complète"
                >
                  <i class="bi bi-eye"></i>
                </button>
              </span>
              <span v-else>{{ truncateValue(setting.value, 100) }}</span>
            </td>
            <td class="text-nowrap">
              <button 
                @click="editSetting(setting.reference)" 
                class="btn btn-sm btn-outline-primary me-1"
                title="Modifier"
              >
                <i class="bi bi-pencil-square"></i>
              </button>
              <button 
                v-if="$userRole == 'Admin'" 
                @click="remove(setting)" 
                class="btn btn-sm btn-outline-danger"
                title="Supprimer"
              >
                <i class="bi bi-trash"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modal for viewing full value -->
    <div class="modal fade" id="valueModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Valeur complète - {{ selectedSetting && selectedSetting.key ? selectedSetting.key : '' }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <pre v-if="selectedSetting" class="bg-light p-3 rounded" style="max-height: 400px; overflow-y: auto;">{{ formatValueForDisplay(selectedSetting.value, selectedSetting.valueType) }}</pre>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Fermer</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  data() {
    return {
      loading: false,
      seeding: false,
      searchQuery: '',
      filterType: '',
      selectedSetting: null,
    };
  },
  computed: {
    ...mapGetters("setting", {
      settings: "getSettings",
    }),
    filteredSettings() {
      let filtered = this.settings || [];
      
      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(setting => {
          return (
            (setting.key && setting.key.toLowerCase().includes(query)) ||
            (setting.reference && setting.reference.toLowerCase().includes(query)) ||
            (setting.value && String(setting.value).toLowerCase().includes(query))
          );
        });
      }
      
      // Filter by type
      if (this.filterType) {
        filtered = filtered.filter(setting => setting.valueType === this.filterType);
      }
      
      return filtered;
    },
  },
  mounted() {
    this.loadSettings();
  },
  methods: {
    async loadSettings() {
      try {
        this.loading = true;
        await this.$store.dispatch("setting/getAll");
      } catch (error) {
        console.error("Error loading settings:", error);
        await this.$store.dispatch("notification/store", {
          status: "error",
          message: "Erreur lors du chargement des paramètres.",
        }, { root: true });
      } finally {
        this.loading = false;
      }
    },
    async refresh() {
      await this.loadSettings();
    },
    async seedDefaults() {
      const confirmed = await this.$confirm({
        message: "Voulez-vous installer les paramètres par défaut ? Les paramètres existants avec les mêmes clés seront mis à jour.",
        button: {
          no: "Annuler",
          yes: "Installer",
        },
      });

      if (!confirmed) {
        return;
      }

      try {
        this.seeding = true;
        await this.$store.dispatch("setting/seedDefaults");
        // Refresh the list after seeding
        await this.loadSettings();
      } catch (error) {
        console.error("Error seeding default settings:", error);
      } finally {
        this.seeding = false;
      }
    },
    filterSettings() {
      // Reactive filtering is handled by computed property
    },
    clearFilters() {
      this.searchQuery = '';
      this.filterType = '';
    },
    editSetting(reference) {
      this.$router.push({
        name: "settings-edit",
        params: {
          reference: reference,
        },
      });
    },
    async remove(data) {
      const setting = data;
      await this.$confirm({
        message: "Voulez-vous vraiment supprimer le paramètre \"" + setting.key + "\" ?",
        button: {
          no: "Non",
          yes: "Oui",
        },
        callback: async (confirm) => {
          if (confirm) {
            try {
              await this.$store.dispatch("setting/destroy", setting.reference);
              // Refresh the list after deletion
              await this.loadSettings();
            } catch (error) {
              console.error("Error deleting setting:", error);
            }
          }
        },
      });
    },
    truncateValue(value, maxLength) {
      if (!value) return '-';
      const str = String(value);
      return str.length > maxLength ? str.substring(0, maxLength) + '...' : str;
    },
    getTypeBadgeClass(valueType) {
      const classes = {
        'string': 'bg-primary',
        'number': 'bg-success',
        'boolean': 'bg-warning',
        'array': 'bg-info',
        'object': 'bg-secondary',
      };
      return classes[valueType] || 'bg-info';
    },
    viewFullValue(setting) {
      this.selectedSetting = setting;
      // Use Bootstrap modal if available, otherwise use browser alert
      if (window.bootstrap) {
        const modal = new window.bootstrap.Modal(document.getElementById('valueModal'));
        modal.show();
      } else {
        alert(this.formatValueForDisplay(setting.value, setting.valueType));
      }
    },
    formatValueForDisplay(value, valueType) {
      if (value === null || value === undefined) return 'null';
      
      if (valueType === 'array' || valueType === 'object') {
        try {
          const parsed = typeof value === 'string' ? JSON.parse(value) : value;
          return JSON.stringify(parsed, null, 2);
        } catch (e) {
          return String(value);
        }
      }
      
      return String(value);
    }
  },
};
</script>